import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaPuzzlePiece, FaTools, FaChartLine } from 'react-icons/fa';
import Modal from './Modal';
import { usePortfolio } from '../context/PortfolioContext';

const Innovations = () => {
  const { portfolioData, loading } = usePortfolio();
  const innovationsData = portfolioData?.innovations || [];
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInnovation, setSelectedInnovation] = useState(null);

  const openModal = (innovation) => {
    setSelectedInnovation(innovation);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedInnovation(null);
  };

  const FeatureIcon = ({ feature }) => {
    switch (feature) {
      case 'problem':
        return <FaPuzzlePiece className="text-primary text-lg" />;
      case 'solution':
        return <FaLightbulb className="text-primary text-lg" />;
      case 'tech':
        return <FaTools className="text-primary text-lg" />;
      case 'impact':
        return <FaChartLine className="text-primary text-lg" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <section id="innovations" className="py-20 bg-gray-900">
        <div className="container">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-700 w-1/3 mb-12 rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((index) => (
                <div key={index} className="bg-gray-800 rounded-lg overflow-hidden">
                  <div className="h-48 bg-gray-700"></div>
                  <div className="p-6 space-y-3">
                    <div className="h-5 bg-gray-700 w-3/4 rounded"></div>
                    <div className="h-4 bg-gray-700 w-full rounded"></div>
                    <div className="h-4 bg-gray-700 w-5/6 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!innovationsData.length) {
    return null;
  }

  return (
    <section id="innovations" className="py-14 bg-gray-900">
      <div className="container">
        <motion.h2 
          className="section-title text-white mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Innovations & AI Builds
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {innovationsData.map((innovation, index) => (
            <motion.div 
              key={innovation.id}
              className="bg-secondary/30 rounded-lg overflow-hidden border border-primary/10 hover:border-primary/30 transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 flex flex-col"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="h-48 overflow-hidden relative">
                <img 
                  src={innovation.image} 
                  alt={innovation.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/20 to-transparent"></div>
              </div>
              
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-xl font-medium text-white mb-2">{innovation.title}</h3>
                <p className="text-gray-300 mb-4">{innovation.summary}</p>
                
                <div className="mb-4 flex-1">
                  <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                    <FeatureIcon feature="problem" />
                    <span className="font-medium">Problem:</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                    {innovation.problem}
                  </p>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                    <FeatureIcon feature="solution" />
                    <span className="font-medium">Solution:</span>
                  </div>
                  <p className="text-gray-400 text-sm line-clamp-2">
                    {innovation.solution}
                  </p>
                </div>
                
                <button
                  onClick={() => openModal(innovation)}
                  className="mt-4 bg-primary/10 hover:bg-primary/20 text-primary px-4 py-2 rounded-lg transition-colors w-full"
                >
                  View Case Study
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {selectedInnovation && (
          <Modal
            isOpen={modalOpen}
            onClose={closeModal}
            title={selectedInnovation.title}
            content={
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 text-white mb-2">
                    <FeatureIcon feature="problem" />
                    <h4 className="font-medium">The Problem</h4>
                  </div>
                  <p className="text-gray-300">{selectedInnovation.problem}</p>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 text-white mb-2">
                    <FeatureIcon feature="solution" />
                    <h4 className="font-medium">The Solution</h4>
                  </div>
                  <p className="text-gray-300">{selectedInnovation.solution}</p>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 text-white mb-2">
                    <FeatureIcon feature="tech" />
                    <h4 className="font-medium">Technologies Used</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedInnovation.tech.map((tech, index) => (
                      <span
                        key={index}
                        className="bg-primary/20 text-primary px-3 py-1 rounded-full text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-2">Development Story</h4>
                  <p className="text-gray-300">{selectedInnovation.developmentStory}</p>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 text-white mb-2">
                    <FeatureIcon feature="impact" />
                    <h4 className="font-medium">Impact</h4>
                  </div>
                  <p className="text-gray-300">{selectedInnovation.impact}</p>
                </div>
                
                {selectedInnovation.demoLink && (
                  <div className="mt-4">
                    <a
                      href={selectedInnovation.demoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Watch Demo
                    </a>
                  </div>
                )}
              </div>
            }
            image={selectedInnovation.image}
          />
        )}
      </div>
    </section>
  );
};

export default Innovations;